'use client';

import EthereumAccount from '#/components/EthereumAccount';
import { Button } from '#/components/ui/button';
import { DialogClose, DialogFooter } from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import { Input } from '#/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { Loader2 } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { formatEther, parseEther } from 'viem';
import * as z from 'zod';
import { BalanceAfterTopUp } from './components/balance-after-top-up';
import { MachineContext } from './contexts/machine';

export function Information() {
  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const target = state.context.target!;
  const { amount } = state.context;

  const availableBalance = useMemo(
    () => parseEther('2048') - parseEther(target.effectiveBalance),
    [target]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        amount: z.string().superRefine((val, ctx) => {
          let ether: bigint | null = null;
          try {
            ether = parseEther(val);
          } catch {}
          if (ether != null) {
            if (ether > availableBalance) {
              ctx.addIssue({
                code: z.ZodIssueCode.too_big,
                maximum: 2048,
                type: 'number',
                inclusive: true,
                message: 'Max stake per validator after top-up: 2048 ETH.',
              });
            }
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: '请输入合法的 ETH',
            });
          }
        }),
      }),
    [availableBalance]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: amount != null ? formatEther(amount) : '0',
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { amount } = values;

    toast.error('This feature is not open yet');
    // dispatch({
    //   type: 'NEXT',
    //   data: {
    //     amount: parseEther(amount),
    //   },
    // });
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <fieldset className="border border-gray-200 p-4 rounded-md">
            <legend className="font-bold px-1">Selected Validator</legend>
            <div className="flex flex-col gap-4">
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">Validator #</dt>
                <dd className="text-sm">#{target?.validatorIndex}</dd>
              </dl>
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">Current Balance</dt>
                <dd className="text-sm">{target?.effectiveBalance}</dd>
              </dl>
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">Withdrawal Address</dt>
                <dd className="text-sm">
                  <EthereumAccount address={target?.withdrawaddr} />
                </dd>
              </dl>
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">Claim Address</dt>
                <dd className="text-sm">
                  <EthereumAccount address={target?.claimaddr} />
                </dd>
              </dl>
            </div>
          </fieldset>
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Top-up Amount</FormLabel>
                <FormControl>
                  <Input {...field} type="number" max="2048" min={0} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <dl className="flex items-center justify-between rounded-lg px-5 py-4  bg-linear-to-r from-[#E9F0FF] via-white to-[#F9F5FF]">
            <dt>Balance after top-up</dt>
            <dd>
              <BalanceAfterTopUp /> ETH
            </dd>
          </dl>
          <DialogFooter className="gap-2 sm:space-x-0">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                <Translation path="Application.Dialog.CancelButtonLabel" />
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="primary"
              disabled={form.formState.isSubmitting}
              onClick={form.handleSubmit(onSubmit)}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {/* <Translation path="Application.Dialog.SubmitButtonLabel" /> */}
              Confirm Top-up
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
